import React from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import RadioButtonCheckedRoundedIcon from "@mui/icons-material/RadioButtonCheckedRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";

export default function ProductImagesUploader({
  existingImages,
  deleteImageIds,
  newImages,
  primaryImageKey,
  onAddImages,
  onRemoveExistingImage,
  onRestoreExistingImage,
  onRemoveNewImage,
  onPrimaryChange,
}) {
  const visibleExisting = existingImages.filter((img) => !deleteImageIds.includes(img.uuid));

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    onAddImages(files);
    event.target.value = "";
  };

  return (
    <Paper
      sx={{
        width: "100%",
        borderRadius: "18px",
        border: "1px solid rgba(148,163,184,0.2)",
        boxShadow: "0 16px 36px rgba(15,23,42,0.05)",
        p: { xs: 2, md: 2.4 },
        bgcolor: "#ffffff",
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={1.2}
        >
          <Box>
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: { xs: 16, md: 17.5 },
                fontWeight: 800,
              }}
            >
              Imágenes
            </Typography>

            <Typography
              sx={{
                color: "#64748b",
                fontSize: 13,
                mt: 0.4,
              }}
            >
              Carga las imágenes del producto y define la imagen principal.
            </Typography>
          </Box>

          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileRoundedIcon />}
            sx={{
              height: 36,
              px: 1.4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "rgba(148,163,184,0.08)",
              },
            }}
          >
            Subir imágenes
            <input hidden multiple accept="image/*" type="file" onChange={handleFileChange} />
          </Button>
        </Stack>

        {!visibleExisting.length && !newImages.length ? (
          <Typography
            sx={{
              color: "#64748b",
              fontSize: 13.2,
              fontWeight: 500,
            }}
          >
            Aún no has agregado imágenes.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                xl: "repeat(4, 1fr)",
              },
              gap: 1.6,
            }}
          >
            {existingImages.map((img) => {
              const removed = deleteImageIds.includes(img.uuid);
              const key = `existing-${img.uuid}`;

              return (
                <Paper
                  key={key}
                  sx={{
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: "1px solid rgba(148,163,184,0.18)",
                    bgcolor: removed ? "rgba(248,250,252,0.65)" : "#ffffff",
                    opacity: removed ? 0.55 : 1,
                    boxShadow: "none",
                  }}
                >
                  <Box
                    sx={{
                      height: 190,
                      bgcolor: "#f8fafc",
                    }}
                  >
                    <Box
                      component="img"
                      src={img.url}
                      alt=""
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>

                  <Stack spacing={1} sx={{ p: 1.2 }}>
                    <Button
                      onClick={() => onPrimaryChange(key)}
                      variant="outlined"
                      startIcon={
                        primaryImageKey === key ? (
                          <RadioButtonCheckedRoundedIcon />
                        ) : (
                          <RadioButtonUncheckedRoundedIcon />
                        )
                      }
                      sx={{
                        height: 34,
                        borderRadius: "9px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12.2,
                        color: "#334155",
                        borderColor: "rgba(51,65,85,0.18)",
                        "&:hover": {
                          borderColor: "rgba(51,65,85,0.28)",
                          bgcolor: "rgba(148,163,184,0.08)",
                        },
                      }}
                    >
                      Principal
                    </Button>

                    {removed ? (
                      <Button
                        onClick={() => onRestoreExistingImage(img.uuid)}
                        variant="outlined"
                        sx={{
                          height: 34,
                          borderRadius: "9px",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: 12.2,
                          color: "#334155",
                          borderColor: "rgba(51,65,85,0.18)",
                          "&:hover": {
                            borderColor: "rgba(51,65,85,0.28)",
                            bgcolor: "rgba(148,163,184,0.08)",
                          },
                        }}
                      >
                        Restaurar
                      </Button>
                    ) : (
                      <Button
                        onClick={() => onRemoveExistingImage(img.uuid)}
                        variant="outlined"
                        startIcon={<DeleteOutlineRoundedIcon />}
                        sx={{
                          height: 34,
                          borderRadius: "9px",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: 12.2,
                          color: "#dc2626",
                          borderColor: "rgba(220,38,38,0.22)",
                          "&:hover": {
                            borderColor: "rgba(220,38,38,0.32)",
                            bgcolor: "rgba(220,38,38,0.06)",
                          },
                        }}
                      >
                        Quitar
                      </Button>
                    )}
                  </Stack>
                </Paper>
              );
            })}

            {newImages.map((img) => (
              <Paper
                key={img.key}
                sx={{
                  borderRadius: "14px",
                  overflow: "hidden",
                  border: "1px solid rgba(148,163,184,0.18)",
                  bgcolor: "#ffffff",
                  boxShadow: "none",
                }}
              >
                <Box
                  sx={{
                    height: 190,
                    bgcolor: "#f8fafc",
                  }}
                >
                  <Box
                    component="img"
                    src={img.preview}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>

                <Stack spacing={1} sx={{ p: 1.2 }}>
                  <Button
                    onClick={() => onPrimaryChange(img.key)}
                    variant="outlined"
                    startIcon={
                      primaryImageKey === img.key ? (
                        <RadioButtonCheckedRoundedIcon />
                      ) : (
                        <RadioButtonUncheckedRoundedIcon />
                      )
                    }
                    sx={{
                      height: 34,
                      borderRadius: "9px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 12.2,
                      color: "#334155",
                      borderColor: "rgba(51,65,85,0.18)",
                      "&:hover": {
                        borderColor: "rgba(51,65,85,0.28)",
                        bgcolor: "rgba(148,163,184,0.08)",
                      },
                    }}
                  >
                    Principal
                  </Button>

                  <Button
                    onClick={() => onRemoveNewImage(img.key)}
                    variant="outlined"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    sx={{
                      height: 34,
                      borderRadius: "9px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 12.2,
                      color: "#dc2626",
                      borderColor: "rgba(220,38,38,0.22)",
                      "&:hover": {
                        borderColor: "rgba(220,38,38,0.32)",
                        bgcolor: "rgba(220,38,38,0.06)",
                      },
                    }}
                  >
                    Quitar
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}